import { CSSProperties, ReactElement } from 'react'
import { I18n, Translate } from 'common'
import { defaultTheme } from 'src/theme'
import { breakpoints } from 'src/theme/breakpoints'
import { useUserStore } from 'src/store/user/UserStore'
import styled from 'styled-components'

export interface Column {
  name: string
  align: 'left' | 'center' | 'right' | 'justify' | 'char' | undefined
  visible: boolean
  clickable?: boolean
  style?: CSSProperties
}

export interface ColumnList {
  columns: Array<Column>
  imageColumnRequired?: boolean
  padding?: string
}

const TableColumn = (
  props: Column & { displayExclTax?: boolean }
): ReactElement | null => {
  const {
    name,
    align,
    visible,
    clickable = true,
    style = {},
    displayExclTax,
  } = props
  if (!visible) {
    return null
  }
  return (
    <StyledColumn align={align} clickable={clickable} style={style}>
      {Translate(name)}
      {displayExclTax && name === 'total' && (
        <ExclTaxText>
          <I18n name="displayExclTax" />
        </ExclTaxText>
      )}
    </StyledColumn>
  )
}

const OrderConfirmationPartsTableHeader = (props: ColumnList): ReactElement => {
  const { columns, imageColumnRequired = true, padding = '28px 16px;' } = props
  const userStore = useUserStore()
  const displayExclTax = userStore?.preferences?.displayExclTax === 'true'
  return (
    <THead>
      <Row padding={padding}>
        {imageColumnRequired && <StyledColumn />}
        {columns.map((column) => (
          <TableColumn
            name={column.name}
            key={column.name}
            align={column.align}
            clickable={column.clickable}
            visible={column.visible}
            style={column.style}
            displayExclTax={displayExclTax}
          />
        ))}
      </Row>
    </THead>
  )
}

export default OrderConfirmationPartsTableHeader

const THead = styled.thead`
  background: #ffffff;
  border-top: 1px solid ${defaultTheme.colors.borderTable};

  @media screen and (max-width: ${breakpoints.tablet}px) {
    display: none;
  }
`
interface RowProps {
  height?: string
  padding: string
}
const Row = styled.tr<RowProps>`
  width: 100%;
  box-shadow: inset 0px -1px 0px ${defaultTheme.colors.greyMedium};

  @media screen and (max-width: ${breakpoints.tablet}px) {
    display: flex;
    padding: ${(p) => p.padding}
    justify-content: space-around;
  }
`
interface ColumnProps {
  clickable?: boolean
  noUppercase?: boolean
}

const StyledColumn = styled.td<ColumnProps>`
  color: ${defaultTheme.colors.columnBorder};
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  text-transform: ${(p) => (p.noUppercase ? 'none' : 'uppercase')};
  padding-top: 10px;
  padding-bottom: 10px;
  text-align: ${(p) => (p.align ? p.align : 'center')};
  cursor: ${(p) => (p.clickable ? 'pointer' : null)};
  vertical-align: top;

  @media print {
    font-size: 10px;
    width: 80px;
  }
`

const ExclTaxText = styled.div`
  font-style: italic;
  font-weight: 400;
  font-size: 11px;
  color: ${defaultTheme.colors.columnBorder};
  text-transform: none;
  text-align: start;
`
